import React from "react"

import { BadgeCheck } from "lucide-react"

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useActiveJob } from "@/contexts/ActiveJobContext"
import { useIsMobile } from "@/hooks/use-mobile"

interface VehicleCollectionTabsProps {
  activeTab: string
  onTabChange: (value: string) => void
  submittedTabs: string[]
  children: React.ReactNode
}

const VehicleCollectionTabs: React.FC<VehicleCollectionTabsProps> = ({
  activeTab,
  onTabChange,
  submittedTabs,
  children,
}) => {
  const isMobile = useIsMobile()
  const { job } = useActiveJob()

  const segmentLabelMap: Record<string, string> = {
    "payment-info": isMobile ? "Payment" : "Payment Info",
    "vehicle-booking": isMobile ? "Booking" : "Vehicle Booking",
  }

  const vehicleCollectionStage = job?.stages?.find((s) => s.id === "vehicle-collection")

  const stageTabs = (vehicleCollectionStage?.segments || []).map((seg) => ({
    value: seg.id,
    // Prefer stage-provided label; fallback to our map, then id
    label: seg.label || segmentLabelMap[seg.id] || seg.id,
    visible: true,
  }))

  const visibleTabs = stageTabs.filter((t) => t.visible)

  // If no segments are configured, show an error message
  if (visibleTabs.length === 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <h3 className="mb-2 text-lg font-semibold text-red-800">Configuration Error</h3>
        <p className="text-red-700">
          Vehicle collection segments are not configured for this job. Please contact support.
        </p>
      </div>
    )
  }

  return (
    <Tabs value={activeTab} onValueChange={onTabChange}>
      <div className="w-full overflow-x-auto">
        <TabsList className={`flex w-full ${isMobile ? "gap-1" : ""}`}>
          {visibleTabs.map((tab) => {
            const isActive = tab.value === activeTab
            const isSubmitted = submittedTabs.includes(tab.value)
            const isDisabled = !isActive // Only active is clickable

            // Show green badge only if submitted AND not currently active
            const showBadge = isSubmitted && !isActive

            const triggerClasses = [
              "group flex-1 min-w-0",
              showBadge ? "bg-green-100" : "",
              isDisabled && !showBadge ? "opacity-50 cursor-not-allowed" : "",
            ]
              .filter(Boolean)
              .join(" ")

            return (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className={triggerClasses}
                disabled={isDisabled}
              >
                <div
                  className="flex w-full min-w-0 items-center justify-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap"
                  title={typeof tab.label === "string" ? tab.label : undefined}
                >
                  {showBadge ? (
                    <BadgeCheck className="h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
                  ) : null}
                  <span className="truncate">{tab.label}</span>
                </div>
              </TabsTrigger>
            )
          })}
        </TabsList>
      </div>
      {children}
    </Tabs>
  )
}

export default VehicleCollectionTabs
